import { User } from "firebase/auth";
import React, { useCallback, useEffect, useState } from "react";
import { Button } from "react-bootstrap";
import toast from "react-hot-toast";
import {
  mapCsvRowToImportedInvestorEntry,
  MappingParams,
} from "../../lib/map-csv-row-to-imported-investor-entry";
import { createImportedInvestor } from "../../services/investorService";
import { ImportedInvestorEntry } from "../../types/ImportedInvestorEntry";
import CsvImporter from "./CsvImporter";
import "./FileImporter.scss";
import ImportMappingParamsForm from "./ImportMappingParamsForm";

function rowToObject(headers: string[], row: string[]) {
  const result: Record<string, string> = {};
  for (let i = 0; i < headers.length; i++) {
    result[headers[i]] = row[i];
  }
  return result;
}

interface FileImporterProps {
  file: File | null;
  fireUser: User;
}

const FileImporter: React.FC<FileImporterProps> = ({
  file: externalFile,
  fireUser,
}) => {
  const [file, setFile] = useState<File | null>();
  const [progressMessage, setProgressMessage] = useState<string>("");
  const [data, setData] = useState<string[][]>([]);
  const [headers, setHeaders] = useState<string[]>([]);
  const [selectedHeaders, setSelectedHeaders] = useState<
    Record<number, boolean>
  >({});

  const [previewEntry, setPreviewEntry] =
    useState<ImportedInvestorEntry | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [importing, setImporting] = useState(false);

  const [importMappingParams, setImportMappingParams] = useState<MappingParams>(
    {
      sourceName: file?.name,
      sourceUrl: file?.name,
      importedByUserId: fireUser.uid,
    },
  );

  useEffect(() => {
    if (file) {
      setImportMappingParams({
        sourceName: file.name,
        sourceUrl: "",
        importedByUserId: fireUser.uid,
      });
    }
  }, [file, fireUser.uid]);

  const handleDataUpdate = useCallback(
    (
      data: string[][],
      headers: string[],
      selectedHeaders: Record<number, boolean>,
    ) => {
      setData(data);
      setHeaders(headers);
      setSelectedHeaders(selectedHeaders);
    },
    [],
  );

  const handleExitPreview = useCallback(() => {
    setProgressMessage(`Viewing ${data.length - 1} rows...`);
    setShowPreview(false);
  }, [data]);

  const handleImportPreview = useCallback(async () => {
    if (!importMappingParams.sourceName || !importMappingParams.sourceUrl) {
      toast.error("Source information is required.");
      return;
    }

    const firstRowObject = rowToObject(data[0], data[1]);
    const mappedEntry = mapCsvRowToImportedInvestorEntry(
      firstRowObject,
      importMappingParams,
    );

    setPreviewEntry(mappedEntry);
    setShowPreview(true);
    setProgressMessage("Previewing import...");
  }, [importMappingParams, data]);

  const handleClose = useCallback(() => {
    setFile(null);
    setData([]);
    setPreviewEntry(null);
    setShowPreview(false);
    setProgressMessage("");
    setImporting(false);
  }, []);

  const handleCloseButtonClick = useCallback(() => {
    if (importing) {
      const result = window.confirm(
        "Are you sure you want to close the importer? This will cancel the import.",
      );
      if (result) {
        handleClose();
        window.location.reload();
      }
    } else {
      handleClose();
    }
  }, [importing, handleClose]);

  const handleImport = useCallback(async () => {
    setImporting(true);

    let i = 0;
    for (const row of data.slice(1)) {
      setProgressMessage(`Importing row ${i + 1} of ${data.length - 1}...`);
      const rowObject = rowToObject(data[0], row);
      const mappedEntry = mapCsvRowToImportedInvestorEntry(
        rowObject,
        importMappingParams,
      );
      await createImportedInvestor(mappedEntry);
      i++;
    }
    toast.success("Import complete!");
    handleClose();
  }, [data, importMappingParams, handleClose]);

  useEffect(() => {
    setFile(externalFile);
  }, [externalFile]);

  if (!file) {
    return null;
  }

  const fileExtension = file.name.split(".").pop();
  return (
    <div className="file-importer">
      <div className="file-importer-header">
        <div
          className="file-importer-header-close"
          onClick={handleCloseButtonClick}
        >
          <span className="material-symbols-outlined">close</span>
        </div>
        <div className="file-importer-header-title">File Importer</div>
        <div className="file-importer-header-subtitle">
          {fileExtension}: <code>{file.name}</code>
        </div>
        {!showPreview && (
          <ImportMappingParamsForm
            onUpdate={setImportMappingParams}
            value={importMappingParams}
          />
        )}
      </div>

      {showPreview ? (
        <div className="mapping-preview">
          <pre>{JSON.stringify(previewEntry, null, 2)}</pre>
        </div>
      ) : (
        <div className="file-importer-body">
          {fileExtension === "csv" && file && (
            <CsvImporter
              file={file}
              previewRowLimit={20}
              headers={headers}
              selectedHeaders={selectedHeaders}
              onDataUpdate={handleDataUpdate}
            />
          )}
        </div>
      )}

      <div className="file-importer-footer">
        <div className="file-importer-footer-progress">
          <span>{progressMessage}</span>
        </div>

        <div className="actions">
          {showPreview ? (
            <>
              <Button
                variant="outline-dark"
                size="lg"
                onClick={handleExitPreview}
                disabled={importing}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                size="lg"
                onClick={handleImport}
                disabled={importing}
              >
                Looks good; import!
              </Button>
            </>
          ) : (
            <Button variant="primary" size="lg" onClick={handleImportPreview}>
              Preview &amp; Import
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default FileImporter;
